import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import SideMenu from './components/SideMenu';
import Home from './views/Home';
import DesafioPage from './views/DesafioPage';
import HistoricoPage from './views/HistoricoPage';
import BetsPage from './views/BetsPage';
import Splash from './views/Splash';
import SetupPage from './views/SetupPage';
import ParceriasPage from './views/ParceriasPage';
import Mundial2026Page from './views/Mundial2026Page';
import ComunidadePage from './views/ComunidadePage';
import PenaltisPage from './views/PenaltisPage';
import { PENALTIS_ENABLED } from './config/features';
import Footer from './components/Footer/Footer';
import Maintenance from './components/Maintenance';

// Backoffice
import { AuthProvider } from './context/AuthContext';
import { TwitchStatusProvider } from './context/TwitchStatusContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './views/Backoffice/Login';
import BackofficeLayout from './views/Backoffice/BackofficeLayout';
import Dashboard from './views/Backoffice/Dashboard';
import { BetsList, BetForm } from './views/Backoffice/Bets';
import { ChallengesList, ChallengeForm } from './views/Backoffice/Challenges';
import { PartnersList, PartnerForm } from './views/Backoffice/Partners';
import { ScoreboardControl } from './views/Backoffice/Scoreboard';
import { ScoreboardOverlay } from './views/ScoreboardOverlay';

// Maintenance mode: set REACT_APP_MAINTENANCE_MODE=true to enable
const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

const ROUTES = {
  home: '/',
  desafio: '/desafio',
  bets: '/bets',
  historico: '/historico',
  setup: '/setup',
  parcerias: '/parcerias',
  mundial2026: '/mundial-2026',
  comunidade: '/comunidade',
  ...(PENALTIS_ENABLED && { penaltis: '/penaltis' }),
};

const PAGE_COMPONENTS = {
  home: Home,
  desafio: DesafioPage,
  bets: BetsPage,
  historico: HistoricoPage,
  setup: SetupPage,
  parcerias: ParceriasPage,
  mundial2026: Mundial2026Page,
  comunidade: ComunidadePage,
  ...(PENALTIS_ENABLED && { penaltis: PenaltisPage }),
};

const getPageFromPath = (pathname) => {
  const route = Object.entries(ROUTES).find(([, path]) => path === pathname);
  return route ? route[0] : 'home';
};

function MainApp() {
  const [showSplash, setShowSplash] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(() => getPageFromPath(location.pathname));

  useEffect(() => {
    setCurrentPage(getPageFromPath(location.pathname));
  }, [location.pathname]);

  const handleNavigation = useCallback((page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
    const path = ROUTES[page] || '/';
    navigate(path);
  }, [navigate]);

  const handleSplashFinish = useCallback(() => {
    setShowSplash(false);
  }, []);

  const CurrentPageComponent = useMemo(() => {
    return PAGE_COMPONENTS[currentPage] || Home;
  }, [currentPage]);

  const pageProps = useMemo(() => ({ onNavigate: handleNavigation }), [handleNavigation]);

  if (isMaintenanceMode) {
    return (
      <div className="App">
        <Maintenance />
      </div>
    );
  }

  if (showSplash) {
    return (
      <div className="App">
        <Splash onFinish={handleSplashFinish} />
      </div>
    );
  }

  return (
    <TwitchStatusProvider>
      <div className="App">
        <div className="App-content">
          <SideMenu onNavigate={handleNavigation} currentPage={currentPage} />
          <div className="page-transition" key={currentPage}>
            <CurrentPageComponent {...pageProps} />
          </div>
        </div>
        <Footer onNavigate={handleNavigation} />
      </div>
    </TwitchStatusProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Backoffice Routes */}
          <Route path="/backoffice/login" element={<Login />} />
          <Route path="/backoffice" element={
            <ProtectedRoute>
              <BackofficeLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Dashboard />} />
            <Route path="bets" element={<BetsList />} />
            <Route path="bets/new" element={<BetForm />} />
            <Route path="bets/:id" element={<BetForm />} />
            <Route path="challenges" element={<ChallengesList />} />
            <Route path="challenges/new" element={<ChallengeForm />} />
            <Route path="challenges/:id" element={<ChallengeForm />} />
            <Route path="partners" element={<PartnersList />} />
            <Route path="partners/new" element={<PartnerForm />} />
            <Route path="partners/:id" element={<PartnerForm />} />
            <Route path="scoreboard" element={<ScoreboardControl />} />
          </Route>

          {/* Scoreboard Overlay (public, for OBS) */}
          <Route path="/scoreboard/overlay" element={<ScoreboardOverlay />} />

          {/* Main App Routes */}
          <Route path="/*" element={<MainApp />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
